// Utility layer (TS-2.5.04) - pure, no side effects. A deliberately small LOCAL copy of the
// WCAG-contrast ink computation `features/roadmap/lib/contrast.ts` already carries (D-247): this
// feature needs exactly one function of it (readableTextColor, for the per-product status-rollup
// chips), and the house convention this batch follows is no-cross-feature-imports - a feature may
// import api/ and shared components/hooks, never another feature's lib. Copying ~25 lines here is
// judged cheaper than promoting the whole roadmap contrast module to a shared location for one
// caller outside roadmap; see decisions.md for the call (next free D-number after D-270).
const INK_HEX = "#2b2b28";
const WHITE_HEX = "#ffffff";
const BLACK_HEX = "#000000";
const AA_NORMAL_TEXT = 4.5;

function parseHexChannels(hex: string): readonly [number, number, number] | null {
  const match = /^#?(?:([0-9a-f]{6})|([0-9a-f]{3}))$/i.exec(hex.trim());
  if (!match) return null;
  const six = match[1] ?? match[2]?.split("").map((c) => c + c).join("");
  if (!six) return null;
  const value = Number.parseInt(six, 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function linearize(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
}

function relativeLuminance(hex: string): number | null {
  const channels = parseHexChannels(hex);
  if (channels === null) return null;
  const [r, g, b] = channels;
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function ratioOfLuminances(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/** The text colour to render on a data-supplied background hex, per D-247's rule (see roadmap/lib/contrast.ts). */
export function readableTextColor(backgroundHex: string): string {
  const background = relativeLuminance(backgroundHex);
  if (background === null) return WHITE_HEX;

  const inkLuminance = relativeLuminance(INK_HEX);
  if (inkLuminance !== null && ratioOfLuminances(background, inkLuminance) >= AA_NORMAL_TEXT) {
    return INK_HEX;
  }
  if (ratioOfLuminances(background, 1) >= AA_NORMAL_TEXT) {
    return WHITE_HEX;
  }
  return BLACK_HEX;
}
